import React from "react";
import { Link } from "react-router-dom";
import AppMenu from "@/components/AppMenu";
import Logo from "@/components/Logo";
import { HERO_DESKTOP, HERO_MOBILE_SKYLINE } from "@/data/site";

/**
 * DESKTOP (>=768px): the exact attached composite image with overlays.
 *   Left UNCHANGED — only wrapped so it renders on md+ screens.
 * MOBILE (<768px): a clean HTML hero (skyline background, badge, headline,
 *   subheading and two full-width stacked buttons). The MacBook mockup is
 *   not shown on mobile.
 */
export default function Hero() {
  return (
    <section className="relative w-full bg-[#0a1f3d]">
      {/* ---------- DESKTOP HERO (unchanged) ---------- */}
      <div className="relative hidden w-full md:block">
        <img
          src={HERO_DESKTOP}
          alt="Online Office Solutions Co — We Build It. We Grow It."
          className="block w-full select-none"
          draggable="false"
        />

        {/* Logo clickable area (over the baked-in logo) */}
        <Link
          to="/"
          aria-label="Online Office Solutions Co home"
          className="absolute left-[2%] top-[1%] h-[11%] w-[22%] cursor-pointer rounded-md"
        />

        {/* Hamburger clickable area (over the baked-in menu icon) */}
        <div className="absolute right-[2.5%] top-[3%] h-[8%] w-[6%]">
          <AppMenu
            trigger={
              <button
                aria-label="Open menu"
                className="h-full w-full cursor-pointer rounded-md bg-transparent outline-none"
              />
            }
          />
        </div>

        {/* Working CTA buttons in the space below the sub-heading */}
        <div className="absolute left-[5.3%] top-[77.6%] flex flex-wrap items-center gap-3 sm:gap-4">
          <Link
            to="/audit"
            className="rounded-lg bg-[#e9a31e] px-4 py-2.5 text-[11px] font-bold text-[#0a1f3d] shadow-lg shadow-black/20 transition-all duration-200 hover:bg-[#f2b743] hover:-translate-y-0.5 sm:px-6 sm:py-3.5 sm:text-sm md:text-base"
          >
            Book Your Free Digital Audit
          </Link>
          <Link
            to="/case-studies"
            className="rounded-lg border border-white/40 bg-white/5 px-4 py-2.5 text-[11px] font-bold text-white backdrop-blur-sm transition-all duration-200 hover:border-white hover:bg-white/10 hover:-translate-y-0.5 sm:px-6 sm:py-3.5 sm:text-sm md:text-base"
          >
            See Client Results
          </Link>
        </div>
      </div>

      {/* ---------- MOBILE HERO (<768px) ---------- */}
      <div className="flex min-h-[calc(100svh-72px)] flex-col bg-[#0a1f3d] md:hidden">
        {/* Top bar: logo + hamburger */}
        <div className="relative z-20 flex shrink-0 items-center justify-between px-5 pt-5 pb-3">
          <Logo />
          <AppMenu
            trigger={
              <button
                aria-label="Open menu"
                className="flex h-11 w-11 items-center justify-center rounded-md text-white"
              >
                <span className="space-y-1.5">
                  <span className="block h-0.5 w-7 bg-white" />
                  <span className="block h-0.5 w-7 bg-white" />
                  <span className="block h-0.5 w-7 bg-white" />
                </span>
              </button>
            }
          />
        </div>

        {/* Skyline image block — shows the full London skyline image
            (uses as much of it as possible) at the box width. */}
        <img
          src={HERO_MOBILE_SKYLINE}
          alt="London skyline"
          className="block w-full shrink-0 select-none"
          draggable="false"
        />

        {/* Content on the dark navy background */}
        <div className="flex flex-1 flex-col items-center justify-center px-6 py-5 text-center">
          <h1 className="text-[2.6rem] font-extrabold leading-[1.05] tracking-tight">
            <span className="block text-white">We Build It.</span>
            <span className="block text-[#e9a31e]">We Grow It.</span>
          </h1>

          <p className="mt-3 max-w-md text-base leading-relaxed text-white/80">
            From a brand new website to ranking #1 on Google — we handle
            everything so you can focus on running your business.
          </p>

          <div id="hero-mobile-cta" className="mt-6 flex w-full max-w-sm flex-col gap-3">
            <Link
              to="/audit"
              className="w-full rounded-lg bg-[#e9a31e] px-6 py-3.5 text-base font-bold text-[#0a1f3d] transition-colors hover:bg-[#f2b743]"
            >
              Book Your Free Digital Audit
            </Link>
            <Link
              to="/case-studies"
              className="w-full rounded-lg border border-white/40 bg-white/5 px-6 py-3.5 text-base font-bold text-white transition-colors hover:bg-white/10"
            >
              See Client Results
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
